import { Button } from "@/components/ui/button";
import { useFileRecordDownload } from "@/hooks/useFileRecordDownload";
import type { FileRecord } from "@cohesive/api-client";
import { Download, ExternalLink, File } from "lucide-react";
import { formatFileSize, getFileRecordName } from "../contentType";
import { useFileRecordVersionPreview } from "../versioning/FileRecordVersioningContext";

interface FallbackContentProps {
  record: FileRecord;
  onOpenExternal?: () => void;
}

/**
 * Rendered for content types we don't inline-preview — archives, binaries,
 * rare formats, or records uploaded without a recognizable `content_type`.
 * We still expose Download + Open-in-new-tab so the content is never
 * trapped behind a pane that can only show metadata.
 */
export function FallbackContent({
  record,
  onOpenExternal,
}: FallbackContentProps) {
  const fileName = getFileRecordName(record);
  const { download } = useFileRecordDownload();
  const { previewFetchUrl, previewVersion, isPreviewing } =
    useFileRecordVersionPreview();

  const handleDownload = () => {
    if (!record.download_url) return;
    void download(record.download_url, fileName || undefined);
  };

  return (
    <div className="flex flex-col items-center justify-center h-full gap-6 p-8">
      <div className="p-6 rounded-2xl bg-muted/30">
        <File className="h-16 w-16 text-muted-foreground" />
      </div>
      <div className="text-center">
        <p className="font-medium text-foreground">Preview not available</p>
        <p className="text-sm text-muted-foreground mt-1">
          This file type cannot be previewed in the browser.
        </p>
      </div>
      {(record.download_url ||
        onOpenExternal ||
        (isPreviewing && previewFetchUrl)) && (
        <div className="flex flex-wrap justify-center gap-3">
          {record.download_url && !isPreviewing && (
            <Button
              variant="outline"
              onClick={handleDownload}
              className="gap-2"
            >
              <Download className="h-4 w-4" />
              Download
            </Button>
          )}
          {isPreviewing && previewFetchUrl && (
            <>
              <Button
                variant="outline"
                onClick={() =>
                  window.open(previewFetchUrl, "_blank", "noopener,noreferrer")
                }
                className="gap-2"
              >
                <ExternalLink className="h-4 w-4" />
                Open version preview
              </Button>
              {previewVersion?.download_url && (
                <Button
                  variant="outline"
                  onClick={() =>
                    void download(
                      previewVersion.download_url!,
                      fileName || undefined,
                    )
                  }
                  className="gap-2"
                >
                  <Download className="h-4 w-4" />
                  Download this version
                </Button>
              )}
            </>
          )}
          {onOpenExternal && !isPreviewing && (
            <Button
              variant="outline"
              onClick={onOpenExternal}
              className="gap-2"
            >
              <ExternalLink className="h-4 w-4" />
              Open in new tab
            </Button>
          )}
        </div>
      )}

      <div className="mt-4 p-4 rounded-lg bg-muted/20 w-full max-w-sm">
        <h3 className="text-sm font-medium text-foreground mb-3">
          File Details
        </h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Name</span>
            <span className="text-foreground truncate ml-4 max-w-[200px]">
              {fileName}
            </span>
          </div>
          {record.size > 0 && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Size</span>
              <span className="text-foreground">
                {formatFileSize(record.size)}
              </span>
            </div>
          )}
          {record.content_type && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Type</span>
              <span className="text-foreground">{record.content_type}</span>
            </div>
          )}
          {record.status && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Status</span>
              <span className="text-foreground capitalize">
                {record.status}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
